import React, { useEffect, useRef, useState, useCallback } from 'react';
import './ContextMenu.css';

/**
 * ContextMenu Component
 * 
 * A right-click context menu that displays at mouse coordinates and provides
 * quick access to file/folder actions.
 * 
 * Requirements:
 * - 11.5: Close when the user clicks elsewhere or presses Escape
 * 
 * Features:
 * - Positioned at mouse coordinates with viewport edge handling
 * - Closes on click outside or Escape key
 * - Keyboard navigation with arrow keys and Enter to select
 * - Support for dividers between groups of items
 * - Support for disabled items
 * - Support for icons on menu items
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Whether the menu is shown
 * @param {number} props.x - X coordinate for menu position
 * @param {number} props.y - Y coordinate for menu position
 * @param {Array} props.items - Array of menu items with label, onClick, icon (optional), disabled (optional), divider (optional)
 * @param {Function} props.onClose - Callback when menu should close
 */
const ContextMenu = ({
  visible,
  x,
  y,
  items = [],
  onClose
}) => {
  const menuRef = useRef(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [adjustedPosition, setAdjustedPosition] = useState({ x, y });

  /**
   * Get the list of actionable (non-divider, non-disabled) item indices
   */
  const getActionableIndices = useCallback(() => {
    return items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => !item.divider && !item.disabled)
      .map(({ index }) => index);
  }, [items]);

  /**
   * Adjust menu position to stay within viewport bounds
   */
  useEffect(() => {
    if (visible && menuRef.current) {
      const menu = menuRef.current;
      const menuRect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let adjustedX = x;
      let adjustedY = y;
      
      // Check if menu would overflow right edge
      if (x + menuRect.width > viewportWidth) {
        adjustedX = Math.max(0, viewportWidth - menuRect.width - 10);
      }
      
      // Check if menu would overflow bottom edge
      if (y + menuRect.height > viewportHeight) {
        adjustedY = Math.max(0, viewportHeight - menuRect.height - 10);
      }
      
      // Check if menu would overflow left edge
      if (adjustedX < 0) {
        adjustedX = 10;
      }
      
      // Check if menu would overflow top edge
      if (adjustedY < 0) {
        adjustedY = 10;
      }
      
      setAdjustedPosition({ x: adjustedX, y: adjustedY });
    }
  }, [visible, x, y]);

  /**
   * Reset focused index when menu opens/closes
   */
  useEffect(() => {
    if (visible) {
      setFocusedIndex(-1);
      // Focus the menu for keyboard navigation
      if (menuRef.current) {
        menuRef.current.focus();
      }
    }
  }, [visible]);

  /**
   * Handle click outside to close menu
   * Requirement 11.5: Close when the user clicks elsewhere
   */
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Use mousedown to catch clicks before they propagate
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  /**
   * Handle keyboard navigation
   * Requirement 11.5: Close when user presses Escape
   * Additional: Arrow keys for navigation, Enter to select
   */
  const handleKeyDown = useCallback((event) => {
    if (!visible) return;

    const actionableIndices = getActionableIndices();
    
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
        
      case 'ArrowDown':
        event.preventDefault();
        if (actionableIndices.length > 0) {
          const currentActionableIndex = actionableIndices.indexOf(focusedIndex);
          const nextIndex = currentActionableIndex === -1 
            ? 0 
            : (currentActionableIndex + 1) % actionableIndices.length;
          setFocusedIndex(actionableIndices[nextIndex]);
        }
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        if (actionableIndices.length > 0) {
          const currentActionableIndex = actionableIndices.indexOf(focusedIndex);
          const prevIndex = currentActionableIndex === -1 
            ? actionableIndices.length - 1 
            : (currentActionableIndex - 1 + actionableIndices.length) % actionableIndices.length;
          setFocusedIndex(actionableIndices[prevIndex]);
        }
        break;
        
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (focusedIndex >= 0 && items[focusedIndex] && !items[focusedIndex].disabled && !items[focusedIndex].divider) {
          items[focusedIndex].onClick();
          onClose();
        }
        break;
        
      case 'Tab':
        // Prevent tab from moving focus outside menu
        event.preventDefault();
        break;
        
      default:
        break;
    }
  }, [visible, focusedIndex, items, onClose, getActionableIndices]);

  /**
   * Handle menu item click
   */
  const handleItemClick = useCallback((item, index) => {
    if (item.disabled || item.divider) return;
    
    item.onClick();
    onClose();
  }, [onClose]);

  /**
   * Handle mouse enter on menu item for hover focus
   */
  const handleItemMouseEnter = useCallback((index) => {
    const item = items[index];
    if (!item.disabled && !item.divider) {
      setFocusedIndex(index);
    }
  }, [items]);

  // Don't render if not visible
  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      className="context-menu"
      style={{
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`
      }}
      role="menu"
      aria-label="Context menu"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      data-testid="context-menu"
    >
      <ul className="context-menu-list" role="none">
        {items.map((item, index) => {
          // Render divider
          if (item.divider) {
            return (
              <li
                key={`divider-${index}`}
                className="context-menu-divider"
                role="separator"
                data-testid="context-menu-divider"
              />
            );
          }

          // Render menu item
          const isFocused = focusedIndex === index;
          const isDisabled = item.disabled;

          return (
            <li
              key={item.label || index}
              className={`context-menu-item ${isFocused ? 'focused' : ''} ${isDisabled ? 'disabled' : ''}`}
              role="menuitem"
              aria-disabled={isDisabled}
              tabIndex={-1}
              onClick={() => handleItemClick(item, index)}
              onMouseEnter={() => handleItemMouseEnter(index)}
              data-testid={`context-menu-item-${item.label?.toLowerCase().replace(/\s+/g, '-') || index}`}
            >
              {item.icon && (
                <span className="context-menu-icon" aria-hidden="true">
                  {item.icon}
                </span>
              )}
              <span className="context-menu-label">{item.label}</span>
              {item.shortcut && (
                <span className="context-menu-shortcut">{item.shortcut}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ContextMenu;
